import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react"
import { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions"
import {
  RootState,
  dynamicMiddlewaresInstance,
  registerReducer,
} from "@core/store"
import { stripUndefined } from "@core/utils"
import { baseQueryWithReauth } from "@/modules/app/store"
import {
  Userbot,
  UserbotApi,
  UserbotBatches,
  UserbotCatalog,
  UserbotTemplates,
} from "../interfaces"
import { normalizeReportSteps } from "./reportSteps"
import { normalizeReportProvenance } from "./reportProvenance"

export const tagTypes = [
  "Proxies",
  "Roles",
  "Role",
  "Accounts",
  "Clients",
  "Client",
  "ClientMe",
  "Chains",
  "ActiveReport",
  "ScheduledChains",
  "ScheduledChain",
  "ScheduleRuns",
  "Triggers",
  "Trigger",
  "Memberships",
  "Catalog",
  "Templates",
  "Template",
  "AccountBatches",
  "AccountBatch",
  "ProfileDictionaries",
  "JoinRequests",
  "JoinRequest",
  "JoinRecovery",
  "Dialogs",
  "DialogPasses",
  "MembershipClarifications",
] as const

const reducerPath: string = "userbotApi"

const baseTGStatApiURL = new URL(
  import.meta.env.REACT_APP_TGSTAT_API,
  window.location.origin,
).toString()
const normalizedBase = baseTGStatApiURL.endsWith("/")
  ? baseTGStatApiURL
  : baseTGStatApiURL + "/"
export const baseUrl = `${normalizedBase}userbot/`

const baseQuery: BaseQueryFn = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token
    if (token) {
      headers.set("authorization", `Bearer ${token}`)
    }
    return headers
  },
})

type ApiEndpointBuider = EndpointBuilder<
  typeof baseQuery,
  (typeof tagTypes)[number],
  typeof reducerPath
>

const endpoints = (build: ApiEndpointBuider) => ({
  /* ----------------------------- Прокси ----------------------------- */
  proxies: build.query<
    Userbot.PageRes<Userbot.Proxy>,
    Userbot.ProxiesQueryParams | void
  >({
    query: (params) => ({
      url: `proxies`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    providesTags: ["Proxies"],
  }),
  createProxy: build.mutation<
    Userbot.Proxy,
    UserbotApi.BodyReq<Userbot.CreateProxy>
  >({
    query: ({ body }) => ({ url: `proxies`, method: "POST", body }),
    invalidatesTags: ["Proxies"],
  }),
  updateProxy: build.mutation<
    Userbot.Proxy,
    UserbotApi.IdBodyReq<Userbot.UpdateProxy, number>
  >({
    query: ({ id, body }) => ({ url: `proxies/${id}`, method: "PATCH", body }),
    invalidatesTags: ["Proxies"],
  }),
  deleteProxy: build.mutation<Userbot.AffectedRes, UserbotApi.IdReq<number>>({
    query: ({ id }) => ({ url: `proxies/${id}`, method: "DELETE" }),
    invalidatesTags: ["Proxies"],
  }),
  proxyHealthCheck: build.mutation<
    Userbot.ProxyHealthCheckRes,
    UserbotApi.IdReq<number>
  >({
    query: ({ id }) => ({
      url: `proxies/${id}/health-check`,
      method: "POST",
    }),
  }),

  /* ----------------------------- Роли ----------------------------- */
  roles: build.query<
    Userbot.PageRes<Userbot.Role>,
    Userbot.RolesQueryParams | void
  >({
    query: (params) => ({
      url: `roles`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    providesTags: ["Roles"],
  }),
  role: build.query<Userbot.Role, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `roles/${id}`, method: "GET" }),
    providesTags: ["Role"],
  }),
  createRole: build.mutation<
    Userbot.Role,
    UserbotApi.BodyReq<Userbot.CreateRole>
  >({
    query: ({ body }) => ({ url: `roles`, method: "POST", body }),
    invalidatesTags: ["Roles"],
  }),
  updateRole: build.mutation<
    Userbot.Role,
    UserbotApi.IdBodyReq<Userbot.UpdateRole>
  >({
    query: ({ id, body }) => ({ url: `roles/${id}`, method: "PATCH", body }),
    invalidatesTags: [
      "Roles",
      "Role",
      "Clients",
      "Client",
      "Accounts",
      "Triggers",
      "Trigger",
    ],
  }),
  deleteRole: build.mutation<Userbot.AffectedRes, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `roles/${id}`, method: "DELETE" }),
    invalidatesTags: [
      "Roles",
      "Role",
      "Clients",
      "Client",
      "Accounts",
      "Triggers",
      "Trigger",
    ],
  }),
  addRoleClients: build.mutation<
    Userbot.Role,
    UserbotApi.IdBodyReq<Userbot.RoleClientsPayload>
  >({
    query: ({ id, body }) => ({
      url: `roles/${id}/clients`,
      method: "POST",
      body,
    }),
    invalidatesTags: [
      "Roles",
      "Role",
      "Clients",
      "Client",
      "Accounts",
      "Triggers",
      "Trigger",
    ],
  }),
  removeRoleClients: build.mutation<
    Userbot.Role,
    UserbotApi.IdBodyReq<Userbot.RoleClientsPayload>
  >({
    query: ({ id, body }) => ({
      url: `roles/${id}/clients`,
      method: "DELETE",
      body,
    }),
    invalidatesTags: [
      "Roles",
      "Role",
      "Clients",
      "Client",
      "Accounts",
      "Triggers",
      "Trigger",
    ],
  }),

  /* ----------------------------- Аккаунты ----------------------------- */
  accounts: build.query<
    Userbot.PageRes<Userbot.AccountFeedItem>,
    Userbot.AccountsQueryParams | void
  >({
    query: (params) => ({
      url: `accounts`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    providesTags: ["Accounts"],
  }),
  account: build.query<Userbot.AccountFeedItem, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `accounts/${id}`, method: "GET" }),
    providesTags: (r, e, { id }) => [{ type: "Accounts", id }],
  }),
  updateAccount: build.mutation<
    Userbot.Account,
    UserbotApi.IdBodyReq<Userbot.UpdateAccount>
  >({
    query: ({ id, body }) => ({ url: `accounts/${id}`, method: "PATCH", body }),
    invalidatesTags: ["Accounts"],
  }),
  deleteAccount: build.mutation<Userbot.AffectedRes, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `accounts/${id}`, method: "DELETE" }),
    invalidatesTags: ["Accounts"],
  }),

  /* ----------------------------- Клиенты ----------------------------- */
  // One-shot создание клиента (3 ветки): создают клиент + аккаунт + авторизуют.
  clients: build.query<
    Userbot.PageRes<Userbot.Client>,
    Userbot.ClientsQueryParams | void
  >({
    query: (params) => ({
      url: `clients`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    providesTags: ["Clients"],
  }),
  client: build.query<Userbot.Client, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `clients/${id}`, method: "GET" }),
    providesTags: (r, e, { id }) => [{ type: "Client", id }],
  }),
  phoneLogin: build.mutation<
    Userbot.PhoneLoginStartRes,
    UserbotApi.BodyReq<Userbot.PhoneLoginPayload>
  >({
    query: ({ body }) => ({
      url: `clients/phone-login`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["Clients", "Accounts"],
  }),
  sessionImport: build.mutation<
    Userbot.Client,
    UserbotApi.BodyReq<Userbot.SessionImportPayload>
  >({
    query: ({ body }) => ({
      url: `clients/session-import`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["Clients", "Accounts"],
  }),
  tdataImport: build.mutation<Userbot.Client, { body: FormData }>({
    query: ({ body }) => ({
      url: `clients/tdata-import`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["Clients", "Accounts"],
  }),
  updateClient: build.mutation<
    Userbot.Client,
    UserbotApi.IdBodyReq<Userbot.UpdateClient>
  >({
    query: ({ id, body }) => ({ url: `clients/${id}`, method: "PATCH", body }),
    invalidatesTags: ["Clients", "Client", "Accounts"],
  }),
  deleteClient: build.mutation<Userbot.AffectedRes, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `clients/${id}`, method: "DELETE" }),
    invalidatesTags: ["Clients", "Accounts"],
  }),
  clientMe: build.query<Userbot.ClientMe, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `clients/${id}/me`, method: "GET" }),
    providesTags: ["ClientMe"],
  }),
  // Финишные шаги phone-login — вызываются на clientId из ответа /phone-login.
  clientLoginCode: build.mutation<
    Userbot.LoginStepRes,
    UserbotApi.IdBodyReq<Userbot.LoginCodePayload>
  >({
    query: ({ id, body }) => ({
      url: `clients/${id}/login/code`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["Clients", "Client", "Accounts"],
  }),
  clientLoginPassword: build.mutation<
    Userbot.LoginStepRes,
    UserbotApi.IdBodyReq<Userbot.LoginPasswordPayload>
  >({
    query: ({ id, body }) => ({
      url: `clients/${id}/login/password`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["Clients", "Client", "Accounts"],
  }),
  clientLogout: build.mutation<void, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `clients/${id}/logout`, method: "POST" }),
    invalidatesTags: ["Clients", "Client", "Accounts"],
  }),
  exportClientSession: build.mutation<
    Userbot.SessionExportRes,
    UserbotApi.IdReq
  >({
    query: ({ id }) => ({
      url: `clients/${id}/session/export`,
      method: "POST",
    }),
  }),

  /* ----------------------------- Цепочки ----------------------------- */
  chains: build.query<
    Userbot.PageRes<Userbot.ChainListItem>,
    Userbot.ChainsQueryParams | void
  >({
    query: (params) => ({
      url: `chains`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    providesTags: ["Chains"],
  }),
  createChain: build.mutation<
    Userbot.LaunchResult,
    UserbotApi.BodyReq<Userbot.ChainDto>
  >({
    query: ({ body }) => ({ url: `chains`, method: "POST", body }),
    invalidatesTags: ["Chains", "AccountBatches"],
  }),
  chainReport: build.query<Userbot.ActiveReport, UserbotApi.IdReq>({
    query: ({ id }) => ({
      url: `chains/${id}/report`,
      method: "GET",
    }),
    providesTags: ["ActiveReport"],
    transformResponse: (res: Userbot.ActiveReport) => ({
      ...res,
      ...normalizeReportProvenance(res),
      steps: normalizeReportSteps(res.steps),
    }),
  }),
  finishedChain: build.query<Userbot.FinishedReport, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `chains/finished/${id}`, method: "GET" }),
    providesTags: ["Chains"],
    transformResponse: (res: Userbot.FinishedReport) => ({
      ...res,
      ...normalizeReportProvenance(res),
      steps: normalizeReportSteps(res.steps),
    }),
  }),
  removeChain: build.mutation<void, UserbotApi.IdReq>({
    query: ({ id }) => ({
      url: `chains/${id}`,
      method: "DELETE",
    }),
    invalidatesTags: ["Chains"],
  }),
  finishChain: build.mutation<void, UserbotApi.IdReq>({
    query: ({ id }) => ({
      url: `chains/${id}/finish`,
      method: "PATCH",
    }),
    invalidatesTags: ["Chains"],
  }),
  chainProgress: build.query<Userbot.ChainProgress, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `chains/${id}/progress`, method: "GET" }),
  }),

  /* ----------------------------- Расписания ----------------------------- */
  scheduledChains: build.query<
    Userbot.PageRes<Userbot.ScheduledChain>,
    Userbot.ScheduledChainsQueryParams | void
  >({
    query: (params) => ({
      url: `scheduled-chains`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    providesTags: ["ScheduledChains"],
  }),
  scheduledChain: build.query<Userbot.ScheduledChain, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `scheduled-chains/${id}`, method: "GET" }),
    providesTags: ["ScheduledChain"],
  }),
  createScheduledChain: build.mutation<
    Userbot.ScheduledChain,
    UserbotApi.BodyReq<Userbot.CreateScheduledChain>
  >({
    query: ({ body }) => ({ url: `scheduled-chains`, method: "POST", body }),
    invalidatesTags: ["ScheduledChains"],
  }),
  updateScheduledChain: build.mutation<
    Userbot.ScheduledChain,
    UserbotApi.IdBodyReq<Userbot.UpdateScheduledChain>
  >({
    query: ({ id, body }) => ({
      url: `scheduled-chains/${id}`,
      method: "PATCH",
      body,
    }),
    invalidatesTags: ["ScheduledChains", "ScheduledChain"],
  }),
  deleteScheduledChain: build.mutation<Userbot.AffectedRes, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `scheduled-chains/${id}`, method: "DELETE" }),
    invalidatesTags: ["ScheduledChains"],
  }),
  runScheduledNow: build.mutation<Userbot.ScheduledRunResult, UserbotApi.IdReq>(
    {
      query: ({ id }) => ({
        url: `scheduled-chains/${id}/run-now`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, { id }) => [
        "ScheduledChains",
        "ScheduledChain",
        "Chains",
        "AccountBatches",
        { type: "ScheduleRuns", id },
      ],
    },
  ),

  /* ----------------------------- Триггеры ----------------------------- */
  triggers: build.query<
    Userbot.PageRes<Userbot.Trigger>,
    Userbot.TriggersQueryParams | void
  >({
    query: (params) => ({
      url: `triggers`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    providesTags: ["Triggers"],
  }),
  trigger: build.query<Userbot.Trigger, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `triggers/${id}`, method: "GET" }),
    providesTags: ["Trigger"],
  }),
  createTrigger: build.mutation<
    Userbot.Trigger,
    UserbotApi.BodyReq<Userbot.CreateTrigger>
  >({
    query: ({ body }) => ({ url: `triggers`, method: "POST", body }),
    invalidatesTags: ["Triggers"],
  }),
  updateTrigger: build.mutation<
    Userbot.Trigger,
    UserbotApi.IdBodyReq<Userbot.UpdateTrigger>
  >({
    query: ({ id, body }) => ({ url: `triggers/${id}`, method: "PATCH", body }),
    invalidatesTags: ["Triggers", "Trigger"],
  }),
  deleteTrigger: build.mutation<Userbot.AffectedRes, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `triggers/${id}`, method: "DELETE" }),
    invalidatesTags: ["Triggers"],
  }),

  /* ----------------------------- Ops ----------------------------- */
  spamCheck: build.mutation<Userbot.SpamCheckResult, UserbotApi.IdReq>({
    query: ({ id }) => ({
      url: `clients/${id}/spam-check`,
      method: "POST",
    }),
  }),

  /* ----------------------------- Каталог ----------------------------- */
  catalogPalette: build.query<UserbotCatalog.CatalogPalette, void>({
    query: () => ({ url: `catalog/palette`, method: "GET" }),
    providesTags: ["Catalog"],
    keepUnusedDataFor: 3600,
  }),
  catalogLimits: build.query<UserbotCatalog.CatalogLimits, void>({
    query: () => ({ url: `catalog/limits`, method: "GET" }),
    providesTags: ["Catalog"],
    keepUnusedDataFor: 3600,
  }),
  catalogNodeKinds: build.query<UserbotCatalog.NodeDescriptor[], void>({
    query: () => ({ url: `catalog/node-kinds`, method: "GET" }),
    providesTags: ["Catalog"],
  }),
  catalogConditionOps: build.query<
    UserbotCatalog.ConditionOperatorDescriptor[],
    void
  >({
    query: () => ({ url: `catalog/condition-ops`, method: "GET" }),
    providesTags: ["Catalog"],
  }),
  catalogActions: build.query<UserbotCatalog.ActionDescriptor[], void>({
    query: () => ({ url: `catalog/actions`, method: "GET" }),
    providesTags: ["Catalog"],
  }),
  catalogMatchers: build.query<UserbotCatalog.MatcherDescriptor[], void>({
    query: () => ({ url: `catalog/matchers`, method: "GET" }),
    providesTags: ["Catalog"],
  }),
  catalogEntities: build.query<UserbotCatalog.EntityDescriptor[], void>({
    query: () => ({ url: `catalog/entities`, method: "GET" }),
    providesTags: ["Catalog"],
  }),
  catalogLlmTasks: build.query<UserbotCatalog.CatalogLlmTask[], void>({
    query: () => ({ url: `catalog/llm-tasks`, method: "GET" }),
    providesTags: ["Catalog"],
  }),
  catalogCronPresets: build.query<UserbotCatalog.CronPreset[], void>({
    query: () => ({ url: `catalog/cron-presets`, method: "GET" }),
    providesTags: ["Catalog"],
  }),

  /* ----------------------------- Dry-run валидация ----------------------------- */
  validateChain: build.mutation<
    UserbotCatalog.ValidationResult,
    UserbotApi.BodyReq<Userbot.ChainDto>
  >({
    query: ({ body }) => ({ url: `chains/validate`, method: "POST", body }),
  }),

  /* ----------------------------- Шаблоны ----------------------------- */
  templates: build.query<
    Userbot.PageRes<UserbotTemplates.Template>,
    UserbotTemplates.TemplatesQueryParams | void
  >({
    query: (params) => ({
      url: `templates`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    providesTags: ["Templates"],
  }),
  template: build.query<UserbotTemplates.Template, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `templates/${id}`, method: "GET" }),
    providesTags: ["Template"],
  }),
  createTemplate: build.mutation<
    UserbotTemplates.Template,
    UserbotApi.BodyReq<UserbotTemplates.CreateTemplateDto>
  >({
    query: ({ body }) => ({ url: `templates`, method: "POST", body }),
    invalidatesTags: ["Templates"],
  }),
  updateTemplate: build.mutation<
    UserbotTemplates.Template,
    UserbotApi.IdBodyReq<UserbotTemplates.UpdateTemplateDto>
  >({
    query: ({ id, body }) => ({
      url: `templates/${id}`,
      method: "PATCH",
      body,
    }),
    invalidatesTags: ["Templates", "Template"],
  }),
  deleteTemplate: build.mutation<Userbot.AffectedRes, UserbotApi.IdReq>({
    query: ({ id }) => ({ url: `templates/${id}`, method: "DELETE" }),
    invalidatesTags: ["Templates"],
  }),
  previewTemplate: build.mutation<
    UserbotTemplates.TemplatePreviewRes,
    UserbotApi.IdBodyReq<{ inputs: Record<string, unknown> }>
  >({
    query: ({ id, body }) => ({
      url: `templates/${id}/preview`,
      method: "POST",
      body,
    }),
  }),
  runTemplate: build.mutation<
    UserbotTemplates.TemplateRunRes,
    UserbotApi.IdBodyReq<UserbotTemplates.RunTemplateDto>
  >({
    query: ({ id, body }) => ({
      url: `templates/${id}/run`,
      method: "POST",
      body,
    }),
    invalidatesTags: [
      "Chains",
      "ScheduledChains",
      "Triggers",
      "AccountBatches",
    ],
  }),

  /* ------------------ Батчи аккаунтов (дельта A) ------------------ */
  accountBatches: build.query<
    Userbot.PageRes<UserbotBatches.AccountBatchJob>,
    UserbotBatches.AccountBatchesQueryParams | void
  >({
    query: (params) => ({
      url: `account-batches`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    transformResponse: (
      res: Userbot.PageRes<UserbotBatches.AccountBatchJob>,
    ) => ({
      items: res.items.map(UserbotBatches.normalizeAccountBatchJob),
      total: res.total,
    }),
    providesTags: ["AccountBatches"],
  }),
  accountBatch: build.query<
    UserbotBatches.AccountBatchDetail,
    UserbotApi.IdReq & UserbotBatches.AccountBatchDetailQueryParams
  >({
    query: ({ id, status, limit, offset }) => ({
      url: `account-batches/${id}`,
      method: "GET",
      params: stripUndefined({ status, limit, offset }),
    }),
    transformResponse: (res: UserbotBatches.AccountBatchDetail) => {
      const items = (res.items ?? []).map(
        UserbotBatches.normalizeAccountBatchItem,
      )
      return {
        ...UserbotBatches.normalizeAccountBatchJob(res),
        items,
        itemsTotal: res.itemsTotal ?? items.length,
      }
    },
    providesTags: (r, e, { id }) => [{ type: "AccountBatch", id }],
  }),
  createAccountBatch: build.mutation<
    UserbotBatches.CreateAccountBatchRes,
    UserbotApi.BodyReq<UserbotBatches.CreateAccountBatchDto>
  >({
    query: ({ body }) => ({ url: `account-batches`, method: "POST", body }),
    transformResponse: UserbotBatches.normalizeCreateAccountBatchRes,
    invalidatesTags: ["AccountBatches", "Chains"],
  }),
  cancelAccountBatch: build.mutation<
    UserbotBatches.AccountBatchJob,
    UserbotApi.IdReq
  >({
    query: ({ id }) => ({
      url: `account-batches/${id}/cancel`,
      method: "POST",
    }),
    transformResponse: UserbotBatches.normalizeAccountBatchJob,
    invalidatesTags: (r, e, { id }) => [
      { type: "AccountBatch", id },
      "AccountBatches",
    ],
  }),
  retryAccountBatchFailed: build.mutation<
    UserbotBatches.RetryAccountBatchRes,
    UserbotApi.IdReq
  >({
    query: ({ id }) => ({
      url: `account-batches/${id}/retry-failed`,
      method: "POST",
    }),
    transformResponse: (res: UserbotBatches.RetryAccountBatchRes) => ({
      job: UserbotBatches.normalizeAccountBatchJob(res.job),
      replayed: res.replayed,
    }),
    invalidatesTags: (r, e, { id }) => [
      { type: "AccountBatch", id },
      "AccountBatches",
      "Chains",
    ],
  }),

  /* ------------------ Словари профилей (дельта B) ------------------ */
  profileDictionaries: build.query<
    Userbot.PageRes<UserbotBatches.ProfileDictItem>,
    UserbotBatches.ProfileDictionariesQueryParams | void
  >({
    query: (params) => ({
      url: `profile-dictionaries`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    providesTags: ["ProfileDictionaries"],
  }),
  createProfileDictionary: build.mutation<
    UserbotBatches.ProfileDictItem,
    UserbotApi.BodyReq<UserbotBatches.CreateProfileDictDto>
  >({
    query: ({ body }) => ({
      url: `profile-dictionaries`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["ProfileDictionaries"],
  }),
  importProfileDictionaries: build.mutation<
    UserbotBatches.ImportProfileDictRes,
    UserbotApi.BodyReq<UserbotBatches.ImportProfileDictDto>
  >({
    query: ({ body }) => ({
      url: `profile-dictionaries/import`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["ProfileDictionaries"],
  }),
  deleteProfileDictionary: build.mutation<
    UserbotBatches.DeleteProfileDictRes,
    UserbotApi.IdReq
  >({
    query: ({ id }) => ({
      url: `profile-dictionaries/${id}`,
      method: "DELETE",
    }),
    invalidatesTags: ["ProfileDictionaries"],
  }),

  /* ------------------ Промоция запаски (дельта C) ------------------ */
  promoteClient: build.mutation<
    Userbot.Client,
    UserbotApi.IdBodyReq<UserbotBatches.PromoteClientDto>
  >({
    query: ({ id, body }) => ({
      url: `clients/${id}/promote`,
      method: "POST",
      body,
    }),
    invalidatesTags: [
      "Roles",
      "Role",
      "Clients",
      "Client",
      "Accounts",
      "Triggers",
      "Trigger",
    ],
  }),

  /* ----------------------------- Членство ----------------------------- */
  memberships: build.query<
    Userbot.PageRes<Userbot.Membership>,
    Userbot.MembershipsQueryParams | void
  >({
    query: (params) => ({
      url: `memberships`,
      method: "GET",
      params: params ? stripUndefined(params) : undefined,
    }),
    providesTags: ["Memberships"],
  }),
  membership: build.query<
    Userbot.Membership | null,
    { accountId: string; chatId: string }
  >({
    query: ({ accountId, chatId }) => ({
      url: `memberships/${accountId}/${chatId}`,
      method: "GET",
    }),
    providesTags: ["Memberships"],
  }),
})

export const userbotApi = createApi({
  reducerPath,
  tagTypes,
  baseQuery: baseQueryWithReauth(baseQuery),
  endpoints: (build) => ({
    ...endpoints(build),
  }),
})
dynamicMiddlewaresInstance.addMiddleware(userbotApi.middleware)
registerReducer(userbotApi.reducerPath, userbotApi.reducer)

export const {
  useProxiesQuery,
  useCreateProxyMutation,
  useUpdateProxyMutation,
  useDeleteProxyMutation,
  useProxyHealthCheckMutation,
  useRolesQuery,
  useRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useAddRoleClientsMutation,
  useRemoveRoleClientsMutation,
  useAccountsQuery,
  useAccountQuery,
  useLazyAccountQuery,
  useUpdateAccountMutation,
  useDeleteAccountMutation,
  useClientsQuery,
  useClientQuery,
  useLazyClientQuery,
  usePhoneLoginMutation,
  useSessionImportMutation,
  useTdataImportMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useClientMeQuery,
  useClientLoginCodeMutation,
  useClientLoginPasswordMutation,
  useClientLogoutMutation,
  useExportClientSessionMutation,
  useChainsQuery,
  useLazyChainsQuery,
  useCreateChainMutation,
  useChainReportQuery,
  useChainProgressQuery,
  useFinishedChainQuery,
  useRemoveChainMutation,
  useFinishChainMutation,
  useScheduledChainsQuery,
  useScheduledChainQuery,
  useLazyScheduledChainQuery,
  useCreateScheduledChainMutation,
  useUpdateScheduledChainMutation,
  useDeleteScheduledChainMutation,
  useRunScheduledNowMutation,
  useTriggersQuery,
  useTriggerQuery,
  useLazyTriggerQuery,
  useCreateTriggerMutation,
  useUpdateTriggerMutation,
  useDeleteTriggerMutation,
  useSpamCheckMutation,
  useMembershipsQuery,
  useMembershipQuery,
  useCatalogPaletteQuery,
  useCatalogLimitsQuery,
  useCatalogNodeKindsQuery,
  useCatalogConditionOpsQuery,
  useCatalogActionsQuery,
  useCatalogMatchersQuery,
  useCatalogEntitiesQuery,
  useCatalogLlmTasksQuery,
  useCatalogCronPresetsQuery,
  useValidateChainMutation,
  useTemplatesQuery,
  useTemplateQuery,
  useLazyTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  usePreviewTemplateMutation,
  useRunTemplateMutation,
  useAccountBatchesQuery,
  useAccountBatchQuery,
  useCreateAccountBatchMutation,
  useCancelAccountBatchMutation,
  useRetryAccountBatchFailedMutation,
  useProfileDictionariesQuery,
  useCreateProfileDictionaryMutation,
  useImportProfileDictionariesMutation,
  useDeleteProfileDictionaryMutation,
  usePromoteClientMutation,
  useLazyProxiesQuery,
  useLazyRolesQuery,
  useLazyAccountsQuery,
  useLazyClientsQuery,
} = userbotApi
